import React, { useEffect, useState } from "react";
import {
  Check,
  Database,
  Layout,
  Loader2,
  MousePointer2,
  Plus,
  Server,
  User,
  Zap,
} from "lucide-react";

type ViewState = "flow" | "timeline" | "list";
type TaskStatus = "pending" | "running" | "completed";

interface Task {
  id: string;
  label: string;
  type: "manual" | "auto";
  icon: React.ReactNode;
}

const tasks: Task[] = [
  {
    id: "t1",
    label: "Maintenance Page",
    type: "manual",
    icon: <Layout size={16} />,
  },
  { id: "t2", label: "Migrate DB", type: "auto", icon: <Database size={16} /> },
  { id: "t3", label: "Deploy App", type: "auto", icon: <Server size={16} /> },
  {
    id: "t4",
    label: "Verify Health",
    type: "manual",
    icon: <User size={16} />,
  },
];

export const HeroAnimation: React.FC = () => {
  const [view, setView] = useState<ViewState>("flow");
  const [taskStatus, setTaskStatus] = useState<Record<string, TaskStatus>>({
    t1: "pending",
    t2: "pending",
    t3: "pending",
    t4: "pending",
  });
  const [visibleCount, setVisibleCount] = useState(3);
  const [cursor, setCursor] = useState({
    x: "50%",
    y: "120%",
    visible: true,
    clicking: false,
  });
  const [isResetting, setIsResetting] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isTimelineProgressing, setIsTimelineProgressing] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const runSequence = async () => {
      // 0. RESET
      setView("flow");
      setTaskStatus({
        t1: "pending",
        t2: "pending",
        t3: "pending",
        t4: "pending",
      });
      setVisibleCount(3);
      setShowSuccess(false);
      setIsTimelineProgressing(false);
      setCursor({ x: "50%", y: "120%", visible: true, clicking: false });

      // Fade In
      // setIsResetting(false); <-- Moved down
      await new Promise((r) => setTimeout(r, 1000));
      setIsResetting(false);

      // 1. ADD TASK ANIMATION
      // Move cursor to placeholder position (T4 is now at 80% top)
      setCursor({ x: "50%", y: "80%", visible: true, clicking: false });
      await new Promise((r) => setTimeout(r, 800));

      // Click
      setCursor((prev) => ({ ...prev, clicking: true }));
      await new Promise((r) => setTimeout(r, 200));
      setVisibleCount(4); // Task appears
      await new Promise((r) => setTimeout(r, 200));
      setCursor((prev) => ({ ...prev, clicking: false }));

      // Move cursor away
      await new Promise((r) => setTimeout(r, 500));
      setCursor({ x: "80%", y: "120%", visible: true, clicking: false });
      await new Promise((r) => setTimeout(r, 1000));

      // 2. MORPH TO TIMELINE
      setView("timeline");
      await new Promise((r) => setTimeout(r, 200));
      setIsTimelineProgressing(true); // Start timeline progress for first task
      await new Promise((r) => setTimeout(r, 2300));

      // 3. MORPH TO LIST
      setView("list");
      setIsTimelineProgressing(false);
      await new Promise((r) => setTimeout(r, 1000));

      // 4. EXECUTION
      // T1 Running
      setTaskStatus((prev) => ({ ...prev, t1: "running" }));
      await new Promise((r) => setTimeout(r, 800));
      setTaskStatus((prev) => ({ ...prev, t1: "completed" }));

      // T2 & T3 Running (Parallel)
      await new Promise((r) => setTimeout(r, 300));
      setTaskStatus((prev) => ({ ...prev, t2: "running", t3: "running" }));
      await new Promise((r) => setTimeout(r, 1500));
      setTaskStatus((prev) => ({ ...prev, t2: "completed" }));
      await new Promise((r) => setTimeout(r, 500));
      setTaskStatus((prev) => ({ ...prev, t3: "completed" }));

      // T4 Ready - Manual Intervention
      await new Promise((r) => setTimeout(r, 500));

      // Move cursor to T4
      setCursor({ x: "70%", y: "80%", visible: true, clicking: false });
      await new Promise((r) => setTimeout(r, 800));

      // Click T4
      setCursor((prev) => ({ ...prev, clicking: true }));
      await new Promise((r) => setTimeout(r, 200));
      setTaskStatus((prev) => ({ ...prev, t4: "completed" }));
      setShowSuccess(true);
      await new Promise((r) => setTimeout(r, 200));
      setCursor((prev) => ({ ...prev, clicking: false }));

      // Move cursor away
      await new Promise((r) => setTimeout(r, 500));
      setCursor({ x: "50%", y: "120%", visible: true, clicking: false });

      // Hold success state
      await new Promise((r) => setTimeout(r, 3000));

      // Fade Out Everything
      setIsResetting(true);
      await new Promise((r) => setTimeout(r, 1000));

      // Loop
      timeout = setTimeout(runSequence, 100);
    };

    runSequence();

    return () => clearTimeout(timeout);
  }, []);

  // Calculate task positions based on View
  const getTaskStyle = (id: string, index: number) => {
    let style: React.CSSProperties = {
      transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "absolute",
    };

    if (view === "flow") {
      // Flow Diagram Layout - Spaced out more vertically
      const positions: Record<string, React.CSSProperties> = {
        t1: { top: "20%", left: "50%", width: "180px", height: "72px" },
        t2: { top: "50%", left: "25%", width: "160px", height: "72px" },
        t3: { top: "50%", left: "75%", width: "160px", height: "72px" },
        t4: { top: "80%", left: "50%", width: "180px", height: "72px" },
      };

      const pos = positions[id];
      style = {
        ...style,
        ...pos,
        transform: "translate(-50%, -50%)",
        opacity: index < visibleCount ? 1 : 0,
        scale: index < visibleCount ? "1" : "0.8",
      };
    } else if (view === "timeline") {
      // Gantt/Timeline Layout - Adjusted for text visibility
      const positions: Record<string, React.CSSProperties> = {
        t1: { top: "20%", left: "5%", width: "25%" }, // Increased width for "Maintenance Page"
        t2: { top: "35%", left: "32%", width: "30%" }, // Starts after T1
        t3: { top: "50%", left: "32%", width: "35%" }, // Parallel with T2
        t4: { top: "65%", left: "70%", width: "25%" }, // Starts after T2/T3
      };

      const pos = positions[id];
      style = {
        ...style,
        ...pos,
        height: "32px",
        transform: "translate(0, -50%)",
        opacity: 1,
      };
    } else if (view === "list") {
      // Vertical List Layout
      style = {
        ...style,
        top: `${20 + index * 20}%`,
        left: "50%",
        width: "90%",
        height: "56px",
        transform: "translate(-50%, -50%)",
        opacity: 1,
      };
    }

    return style;
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is standard md breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div
      className="w-full max-w-2xl mx-auto h-[380px] sm:h-[400px] bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-white/10 shadow-2xl overflow-hidden relative selection:bg-none group transition-transform duration-500 ease-out"
      style={{
        transform: isMobile
          ? "none"
          : "perspective(800px) rotateY(-18deg) rotateX(8deg) scale(1.15)",
        transformStyle: "preserve-3d",
      }}
    >
      {/* Static reflection overlay - responsive to mode */}
      <div className="absolute inset-0 z-backdrop pointer-events-none overflow-hidden rounded-xl bg-[linear-gradient(115deg,rgba(0,0,0,0.01)_-5%,rgba(255,255,255,0.005)_-5%,rgba(255,255,255,0.025)_62.5%,rgba(0,0,0,0.01)_62.5%)] dark:bg-[linear-gradient(115deg,transparent_-5%,rgba(255,255,255,0.01)_-5%,rgba(255,255,255,0.05)_62.5%,transparent_62.5%)]" />

      {/* Header Bar */}
      <div className="absolute top-0 left-0 right-0 h-10 bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-white/5 flex items-center px-4 justify-between z-header backdrop-blur-sm">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
        </div>
        <div className="text-xs font-mono text-slate-400 dark:text-slate-500 flex items-center gap-2 transition-all duration-500">
          <span
            className={
              view === "flow"
                ? "text-indigo-600 dark:text-indigo-400 font-bold"
                : ""
            }
          >
            DESIGN
          </span>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <span
            className={
              view === "timeline"
                ? "text-indigo-600 dark:text-indigo-400 font-bold"
                : ""
            }
          >
            PLAN
          </span>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <span
            className={
              view === "list"
                ? "text-indigo-600 dark:text-indigo-400 font-bold"
                : ""
            }
          >
            RUN
          </span>
        </div>
      </div>

      {/* Main Content Wrapper */}
      <div
        className="absolute inset-0 top-10 p-4 transition-opacity duration-1000 ease-in-out"
        style={{
          opacity: isResetting ? 0 : showSuccess ? 0.1 : 1,
          filter: showSuccess ? "grayscale(50%) blur(2px)" : "none",
        }}
      >
        {/* Background Grid */}
        <div
          className={`absolute inset-0 opacity-10 transition-opacity duration-1000 ${view === "timeline" ? "opacity-20" : "opacity-5"}`}
        >
          <div
            className="w-full h-full"
            style={{
              backgroundImage:
                "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          ></div>
          <div
            className="absolute inset-0 dark:block hidden"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          ></div>
        </div>

        {/* Timeline Markers */}
        <div
          className={`absolute top-2 left-0 w-full flex justify-between px-8 text-[10px] text-slate-400 dark:text-slate-600 font-mono transition-opacity duration-500 ${view === "timeline" ? "opacity-100" : "opacity-0"}`}
        >
          <span>00:00</span>
          <span>00:05</span>
          <span>00:10</span>
          <span>00:15</span>
        </div>

        {/* Flow Connectors (SVG Overlay) */}
        <svg
          className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-500 ${view === "flow" ? "opacity-100" : "opacity-0"}`}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* T1 Bottom (50, 29) to T2 Top (25, 41) - Dashed and Curvy */}
          <path
            d="M 50 29 C 50 38, 25 32, 25 41"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="0.5"
            strokeDasharray="2 2"
          />
          {/* T1 Bottom (50, 29) to T3 Top (75, 41) - Dashed and Curvy */}
          <path
            d="M 50 29 C 50 38, 75 32, 75 41"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="0.5"
            strokeDasharray="2 2"
          />

          {/* T2 Bottom (25, 59) to T4 Top (50, 71) - Dashed and Curvy */}
          <path
            d="M 25 59 C 25 68, 50 62, 50 71"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="0.5"
            strokeDasharray="2 2"
            className={`transition-opacity duration-500 ${visibleCount === 4 ? "opacity-100" : "opacity-0"}`}
          />
          {/* T3 Bottom (75, 59) to T4 Top (50, 71) - Dashed and Curvy */}
          <path
            d="M 75 59 C 75 68, 50 62, 50 71"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="0.5"
            strokeDasharray="2 2"
            className={`transition-opacity duration-500 ${visibleCount === 4 ? "opacity-100" : "opacity-0"}`}
          />
        </svg>

        {/* Placeholder for T4 (Add Task Button) */}
        <div
          className={`absolute top-[80%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[180px] h-[72px] 
            rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center gap-2 text-slate-400 dark:text-slate-600
            transition-all duration-300
            ${view === "flow" && visibleCount < 4 ? "opacity-100 scale-100" : "opacity-0 scale-90 pointer-events-none"}
            `}
        >
          <Plus size={16} />
          <span className="text-sm font-medium">Add Step</span>
        </div>

        {/* Tasks */}
        {tasks.map((task, index) => {
          const status = taskStatus[task.id];
          const isCompleted = status === "completed";
          const isRunning = status === "running";

          return (
            <div
              key={task.id}
              className={`flex items-center bg-white dark:bg-slate-800 border backdrop-blur-md z-decorator relative
                ${view === "flow" ? "rounded-xl border-indigo-500/20 shadow-lg justify-center gap-2 p-2" : ""}
                ${view === "timeline" ? "rounded border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10 px-3 overflow-hidden" : ""}
                ${view === "list" ? "rounded-lg px-4 border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 overflow-hidden" : ""}
                ${isCompleted && view === "list" ? "border-green-500/50 bg-green-50 dark:bg-green-500/5" : ""}
                ${isRunning && view === "list" ? "border-indigo-500/50 bg-indigo-50 dark:bg-indigo-500/5 shadow-[0_0_15px_rgba(99,102,241,0.1)]" : ""}
              `}
              style={getTaskStyle(task.id, index)}
            >
              {/* Timeline Progress Bar (T1 only) */}
              {view === "timeline" && task.id === "t1" && (
                <div
                  className="absolute inset-y-0 left-0 bg-indigo-500/20 transition-all ease-linear"
                  style={{
                    width: isTimelineProgressing ? "100%" : "0%",
                    transitionDuration: "2300ms",
                  }}
                />
              )}

              <div
                className={`flex items-center w-full relative z-decorator ${view === "flow" ? "justify-center flex-col gap-1 pt-6" : "gap-3"}`}
              >
                <div
                  className={`
                  flex items-center justify-center rounded-full transition-all duration-500 flex-shrink-0
                  ${view === "flow" ? "w-8 h-8 bg-slate-100 dark:bg-slate-700/50 text-indigo-600 dark:text-indigo-300" : "w-6 h-6 bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-300"}
                  ${view === "list" && isCompleted ? "!bg-green-500 !text-white scale-110" : ""}
                  ${view === "list" && isRunning ? "!bg-indigo-500 !text-white animate-pulse" : ""}
                `}
                >
                  {view === "list" && isCompleted ? (
                    <Check size={12} strokeWidth={3} />
                  ) : view === "list" && isRunning ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    React.cloneElement(
                      task.icon as React.ReactElement<{
                        size?: number | string;
                      }>,
                      { size: view === "flow" ? 18 : 14 },
                    )
                  )}
                </div>

                <div
                  className={`flex flex-col ${view === "flow" ? "items-center text-center" : "items-start min-w-0 flex-1"}`}
                >
                  <span
                    className={`font-medium text-slate-900 dark:text-white whitespace-nowrap truncate max-w-full ${view === "timeline" ? "text-xs" : "text-sm"}`}
                  >
                    {task.label}
                  </span>

                  <div
                    className={`transition-all duration-300 overflow-hidden ${view === "timeline" || view === "flow" ? "h-0 opacity-0" : "h-auto opacity-100"}`}
                  >
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono uppercase tracking-wider flex items-center justify-center gap-1">
                      {task.type === "auto" && (
                        <Zap
                          size={10}
                          className="text-yellow-500 dark:text-yellow-400 fill-yellow-500 dark:fill-yellow-400"
                        />
                      )}
                      {task.type}
                    </span>
                  </div>
                </div>

                <div
                  className={`transition-all duration-300 ${view === "list" ? "opacity-100 w-auto" : "opacity-0 w-0"}`}
                >
                  {isCompleted && (
                    <span className="text-xs font-mono text-green-600 dark:text-green-400">
                      DONE
                    </span>
                  )}
                  {isRunning && (
                    <span className="text-xs font-mono text-indigo-600 dark:text-indigo-400 animate-pulse">
                      RUNNING...
                    </span>
                  )}
                  {status === "pending" && (
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-600">
                      WAITING
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Cursor */}
        <div
          className="absolute z-dropdown pointer-events-none transition-all duration-500 ease-in-out"
          style={{
            left: cursor.x,
            top: cursor.y,
            opacity: cursor.visible ? 1 : 0,
            transform: `translate(-20%, -20%) scale(${cursor.clicking ? 0.8 : 1})`,
          }}
        >
          <MousePointer2
            className="text-slate-900 dark:text-white fill-indigo-500 drop-shadow-lg"
            size={24}
          />
        </div>
      </div>

      {/* Completion Message Overlay (No Confetti) */}
      <div
        className={`absolute z-sticky inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-1000 ${showSuccess && !isResetting ? "opacity-100" : "opacity-0"}`}
        style={{ opacity: showSuccess && !isResetting ? 1 : 0 }}
      >
        <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-green-500/30 px-8 py-4 rounded-full flex items-center gap-3 shadow-2xl transform scale-110 relative z-dropdown">
          <div className="bg-green-500 rounded-full p-1.5">
            <Check size={20} className="text-white" strokeWidth={3} />
          </div>
          <div className="flex flex-col">
            <span className="text-green-600 dark:text-green-400 font-bold tracking-wide text-lg">
              SUCCESS
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
